import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const ResetPasswordPage = () => {
  const [otp, setOtp]               = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [loading, setLoading]         = useState(false);
  const [resending, setResending]     = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [countdown, setCountdown]     = useState(60);
  const [showPw, setShowPw]           = useState(false);

  const inputsRef = useRef([]);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { resetPassword, requestPasswordReset } = useAuth();

  const phone_number = location.state?.phone_number || '';

  useEffect(() => {
    if (!phone_number) navigate('/forgot-password');
  }, [phone_number, navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => { inputsRef.current[0]?.focus(); }, []);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      inputsRef.current[3]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const code = otp.join('');
    if (code.length < 4) { setError('Enter the 4-digit OTP.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    const result = await resetPassword(phone_number, code, newPassword);

    if (result.success) {
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(result.message);
      setOtp(['', '', '', '']);
      inputsRef.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    const result = await requestPasswordReset(phone_number);
    if (result.success) { setSuccess('OTP resent.'); setCountdown(60); }
    else setError(result.message);
    setResending(false);
  };

  return (
    <div className="page">
      <div className="card">
        <h2>Reset Password</h2>
        <p className="subtitle">
          Enter the 4-digit OTP sent to <strong>{phone_number}</strong> and choose a new password.
        </p>

        {error   && <div className="error-box">{error}</div>}
        {success && <div className="success-box">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* OTP inputs */}
          <label className="field-label">OTP Code</label>
          <div className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => inputsRef.current[i] = el}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="otp-input"
              />
            ))}
          </div>

          {/* New password */}
          <label className="field-label">New Password</label>
          <div className="pw-wrapper">
            <input
              type={showPw ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min 8 chars, number & special char"
              required
              className="input-field"
            />
            <button type="button" className="eye-btn" onClick={() => setShowPw(p => !p)}>
              {showPw ? '🙈' : '👁️'}
            </button>
          </div>

          {/* Confirm password */}
          <label className="field-label">Confirm Password</label>
          <input
            type={showPw ? 'text' : 'password'}
            value={confirmPassword}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            required
            className="input-field"
          />

          <button type="submit" disabled={loading} className="primary-btn">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="resend-row">
          {countdown > 0 ? (
            <span className="timer">Resend OTP in {countdown}s</span>
          ) : (
            <button onClick={handleResend} disabled={resending} className="resend-btn">
              {resending ? 'Sending...' : 'Resend OTP'}
            </button>
          )}
        </div>

        <p className="footer-link">
          <Link to="/login" className="link">← Back to Login</Link>
        </p>
      </div>

      <style>{`
        .page {
          min-height: 100vh; display: flex; align-items: center; justify-content: center;
          background: radial-gradient(at center, #49AFAF, #A8DFF4);
          font-family: Arial, sans-serif; padding: 20px;
        }
        .card {
          background: white; border-radius: 12px; padding: 40px 32px;
          max-width: 420px; width: 100%; text-align: center;
          box-shadow: 0 4px 24px rgba(0,0,0,0.1);
        }
        h2 { font-size: 24px; font-weight: bold; color: #111827; margin-bottom: 8px; }
        .subtitle { color: #6b7280; font-size: 14px; margin-bottom: 20px; line-height: 1.5; }
        .error-box {
          background: #fee2e2; border: 1px solid #f87171; color: #b91c1c;
          padding: 10px 14px; border-radius: 6px; margin-bottom: 14px; font-size: 14px;
        }
        .success-box {
          background: #d1fae5; border: 1px solid #6ee7b7; color: #065f46;
          padding: 10px 14px; border-radius: 6px; margin-bottom: 14px; font-size: 14px;
        }
        .field-label { display: block; text-align: left; font-size: 13px; color: #374151; margin-bottom: 6px; font-weight: 500; }
        .otp-inputs { display: flex; justify-content: center; gap: 12px; margin-bottom: 20px; }
        .otp-input {
          width: 56px; height: 64px; text-align: center; font-size: 28px; font-weight: bold;
          border: 2px solid #d1d5db; border-radius: 8px; background: #f9fafb;
          color: #111827; outline: none; transition: border-color 0.2s;
        }
        .otp-input:focus { border-color: #3b82f6; background: white; }
        .pw-wrapper { position: relative; margin-bottom: 14px; }
        .pw-wrapper .input-field { margin-bottom: 0; padding-right: 40px; }
        .eye-btn {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; font-size: 18px; padding: 4px;
        }
        .input-field {
          width: 100%; padding: 12px 16px; border: 1px solid #d1d5db;
          border-radius: 6px; font-size: 15px; background: white;
          box-sizing: border-box; margin-bottom: 14px;
        }
        .input-field:focus { outline: none; border-color: #3b82f6; }
        .primary-btn {
          width: 100%; padding: 13px; background: #3b82f6; color: white;
          border: none; border-radius: 50px; font-size: 16px; font-weight: 500;
          cursor: pointer; margin-top: 4px; transition: background 0.2s;
        }
        .primary-btn:hover { background: #2563eb; }
        .primary-btn:disabled { background: #9ca3af; cursor: not-allowed; }
        .resend-row { margin-top: 16px; font-size: 14px; }
        .timer { color: #6b7280; }
        .resend-btn {
          background: none; border: none; color: #3b82f6; cursor: pointer;
          font-size: 14px; font-weight: 500; text-decoration: underline; padding: 0;
        }
        .footer-link { margin-top: 16px; font-size: 14px; }
        .link { color: #3b82f6; text-decoration: none; }
        .link:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;
